/**
 * ImageLightbox Component - Full-screen image viewer
 * 
 * Simple modal/lightbox for viewing images full-screen.
 * Supports closing via X button, ESC key, or clicking outside.
 */
import React, { useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useObjectUrl } from '../hooks/useObjectUrl';

/**
 * Renders the actual image with proper URL handling
 */
function LightboxImage({ fileId, alt }) {
  const objectUrl = useObjectUrl(fileId);
  
  // Handle case where fileId is already a renderable URL
  const isDataUrl = fileId && typeof fileId === 'string' && fileId.startsWith('data:');
  const isHttpUrl = fileId && typeof fileId === 'string' && (fileId.startsWith('http://') || fileId.startsWith('https://'));
  const isBlobUrl = fileId && typeof fileId === 'string' && fileId.startsWith('blob:');
  
  const src = isDataUrl || isHttpUrl || isBlobUrl ? fileId : objectUrl;
  
  if (!src) {
    return (
      <div className="flex items-center justify-center text-muted-foreground">
        Loading...
      </div>
    );
  }
  
  return (
    <img
      src={src}
      alt={alt}
      className="max-h-[90vh] max-w-[90vw] object-contain"
      onClick={(e) => e.stopPropagation()}
    />
  );
}

/**
 * ImageLightbox - Full-screen image viewer modal
 * 
 * @param {boolean} isOpen - Whether the lightbox is open
 * @param {function} onClose - Callback to close the lightbox
 * @param {string[]} imageFileIds - Array of image file IDs
 * @param {number} initialIndex - Initial image index to show
 * @param {string} alt - Alt text for images
 */
export default function ImageLightbox({ 
  isOpen, 
  onClose, 
  imageFileIds = [], 
  initialIndex = 0,
  alt = ''
}) {
  const [currentIndex, setCurrentIndex] = React.useState(initialIndex);
  
  // Reset index when imageFileIds change or lightbox opens
  React.useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
    }
  }, [isOpen, initialIndex]);
  
  // Handle ESC key to close
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowLeft' && imageFileIds.length > 1) {
      setCurrentIndex((prev) => (prev > 0 ? prev - 1 : imageFileIds.length - 1));
    } else if (e.key === 'ArrowRight' && imageFileIds.length > 1) {
      setCurrentIndex((prev) => (prev < imageFileIds.length - 1 ? prev + 1 : 0));
    }
  }, [onClose, imageFileIds.length]);
  
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when lightbox is open
      document.body.style.overflow = 'hidden';
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = '';
      };
    }
  }, [isOpen, handleKeyDown]);
  
  if (!isOpen || !imageFileIds.length) {
    return null;
  }
  
  const currentFileId = imageFileIds[currentIndex];
  const hasMultiple = imageFileIds.length > 1;
  
  const goToPrevious = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : imageFileIds.length - 1));
  };
  
  const goToNext = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev < imageFileIds.length - 1 ? prev + 1 : 0));
  };
  
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Image viewer"
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
        aria-label="Close image viewer"
      >
        <X className="w-6 h-6" />
      </button>
      
      {/* Image counter */}
      {hasMultiple && (
        <div className="absolute top-4 left-4 z-10 px-3 py-1 rounded-full bg-black/50 text-white text-sm">
          {currentIndex + 1} / {imageFileIds.length}
        </div>
      )}
      
      {/* Previous button */}
      {hasMultiple && (
        <button
          onClick={goToPrevious}
          className="absolute left-4 z-10 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
          aria-label="Previous image"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
      )}
      
      {/* Main image */}
      <div className="flex items-center justify-center">
        <LightboxImage fileId={currentFileId} alt={alt} />
      </div>
      
      {/* Next button */}
      {hasMultiple && (
        <button
          onClick={goToNext}
          className="absolute right-4 z-10 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
          aria-label="Next image"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      )}
    </div>
  );
}
